<template>
    <div
        class="w-full flex flex-col md:flex-row justify-between items-start md:items-center mb-4"
    >
        <div class="w-full md:w-auto mb-4 md:mb-0">
            <div
                class="flex flex-col md:flex-row items-start md:items-center mb-2"
            >
                <h2 class="text-slate-800 dark:text-slate-100 text-2xl font-semibold">
                    {{ trip?.title }}
                </h2>
                <p
                    v-if="trip?.is_shared"
                    @click="$emit('share')"
                    class="bg-orange-100 hover:bg-orange-50 text-orange dark:bg-orange-500/20 dark:hover:bg-orange-500/30 dark:text-orange-300 p-2 px-2 rounded-lg flex items-center text-xs font-medium leading-none ml-0 md:ml-2 h-6 gap-1 cursor-pointer mt-2 md:mt-0"
                >
                    <Icon
                        icon="globe"
                        v-bind:width="16"
                        v-bind:height="16"
                        pathClass="text-orange"
                    />
                    Shared
                </p>
            </div>
            <p class="text-slate-500 dark:text-slate-400 text-sm mb-1 flex items-center">
                {{ dayjs(trip?.start_date).format('MMM D') }}
                &ndash;
                {{ dayjs(trip?.end_date).format('MMM D, YYYY') }}
                <span class="px-2 text-slate-500 dark:text-slate-400">
                    <div
                        class="w-0.5 h-0.5 bg-slate-500 rounded-full"
                    ></div>
                </span>
                <span v-if="hasDestinations">
                    {{ formattedDestinations }}
                </span>
                <span v-else-if="trip?.city || trip?.state">
                    {{ trip?.city ? trip?.city : trip?.state }},&nbsp;
                    {{ trip?.country }}
                </span>
            </p>
            <p class="text-slate-800 dark:text-slate-100 text-sm mb-1">
                {{ trip?.description }}
            </p>
        </div>
        <div class="flex flex-row gap-2 items-center">
            <button
                type="button"
                class="btn-base flex items-center gap-2"
                @click="$emit('add-redemption')"
            >
                <Icon icon="plus" />
                <span class="ml-2 whitespace-nowrap">Add Redemption</span>
            </button>
            <button
                type="button"
                class="btn-base flex items-center gap-2"
                @click="$emit('share')"
            >
                <Icon
                    v-if="trip?.is_shared"
                    icon="globe-dashed"
                    v-bind:width="16"
                    v-bind:height="16"
                />
                <Icon
                    v-else
                    icon="globe"
                    v-bind:width="16"
                    v-bind:height="16"
                />
                <span class="hidden md:inline">
                    {{ trip?.is_shared ? 'Unshare' : 'Share' }}
                </span>
            </button>
            <button
                v-if="trip?.is_shared"
                type="button"
                class="btn-base flex items-center gap-2"
                @click="copyShareLink"
                title="Copy share link"
            >
                <i class="fa-regular fa-clone"></i>
            </button>
            <TripMoreActionsBtn
                :show-label="false"
                :show-share="false"
                :trip="trip"
                class="h-10"
            />
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import dayjs from 'dayjs';
import { notify } from 'notiwind';
import Icon from '@/components/ui/icon/icon.vue';
import TripMoreActionsBtn from '@/components/trips/trip-more-actions-btn.vue';
import HsTracking from '@/services/hs-tracking';

const props = defineProps({
    trip: { type: Object, required: true },
});

defineEmits(['share', 'add-redemption']);

const copyShareLink = () => {
    const url = `${document.location.origin}/trip/shared/${props.trip.share_token}`;
    navigator.clipboard.writeText(url).then(() => {
        notify({ group: 'success', title: 'Link copied', text: 'Share link copied to clipboard.' }, 3000);
        HsTracking.trackCustomEvent('pe22692776_copied_shareable_trip_link', { trip: props.trip });
    }).catch(() => {
        notify({ group: 'error', title: 'Copy failed', text: 'Unable to copy the link. Please try again.' }, 3000);
    });
};

const hasDestinations = computed(() => {
    return (
        props.trip?.destinations &&
        Array.isArray(props.trip.destinations) &&
        props.trip.destinations.length > 0
    );
});

const formattedDestinations = computed(() => {
    if (!hasDestinations.value) return '';
    return props.trip.destinations
        .map((dest) => {
            const city = dest.city || '';
            const country = dest.country || '';
            return city ? `${city}, ${country}` : country;
        })
        .join(' – ');
});
</script>
